'use client'

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'

import { ImageHelper } from '@/lib/helpers/image.helper'
import { removeFileExtension } from '@/lib/utils/common'
import { TBannerSlide } from '@/types/home'
import Autoplay from 'embla-carousel-autoplay'
import Image from 'next/image'
import Link from 'next/link'
import { useMemo } from 'react'

type Props = {
  slides: TBannerSlide[]
  isMobile?: boolean
}

export const HeroBanner: React.FC<Props> = ({ slides, isMobile }) => {
  const validSlides = useMemo(
    () =>
      slides.filter((slide) => {
        if (slide.hidden) return false

        if (slide.active_time) {
          const now = new Date().getTime()
          const start = slide.starts_at
            ? new Date(slide.starts_at).getTime()
            : 0
          const end = slide.ends_at
            ? new Date(slide.ends_at).getTime()
            : Infinity
          return now > start && now < end
        }

        return true
      }),
    [slides]
  )

  const itmSourceChange = (url: string, newValue: string): string => {
    try {
      const isRelative = url.startsWith('/')
      const base =
        typeof window !== 'undefined'
          ? window.location.origin
          : 'https://www.coolmate.me'
      const urlObj = new URL(url, base)

      if (urlObj.searchParams.has('itm_source')) {
        urlObj.searchParams.set('itm_source', newValue)
      } else {
        urlObj.searchParams.set('itm_source', 'homepage')
        urlObj.searchParams.set('itm_medium', newValue)
      }

      if (isRelative) {
        const search = urlObj.searchParams.toString()
        return `${urlObj.pathname}${search ? `?${search}` : ''}${urlObj.hash}`
      }

      return urlObj.toString()
    } catch {
      return url
    }
  }

  const buildHeroBannerUrl = (
    slideUrl: string,
    index: number,
    itmCampaignImage: string,
    itmContentImage: string
  ): string => {
    const baseUrl = itmSourceChange(slideUrl, `herobanner_${index}`)
    const isRelative = baseUrl.startsWith('/')
    const origin = 'https://www.coolmate.me'
    const urlObj = new URL(baseUrl, origin)

    urlObj.searchParams.set(
      'itm_campaign',
      removeFileExtension(itmCampaignImage)
    )
    urlObj.searchParams.set('itm_content', removeFileExtension(itmContentImage))

    const searchString = urlObj.searchParams.toString()
    return isRelative
      ? `${urlObj.pathname}${searchString ? `?${searchString}` : ''}${urlObj.hash}`
      : urlObj.toString()
  }

  if (!validSlides.length) return null

  return (
    <section
      className='relative w-full max-sm:min-h-[500px] lg:min-h-[500px]'
      aria-label='Hero Banner Slideshow'
    >
      <h2 className='sr-only'>Featured Promotions and Collections</h2>
      <Carousel
        opts={{
          loop: true,
        }}
        plugins={[
          Autoplay({
            delay: 8000,
            stopOnInteraction: false,
            stopOnMouseEnter: true,
          }),
        ]}
      >
        <CarouselContent className='ml-0 h-full w-full'>
          {validSlides.map((slide, index) => {
            const itm_campaign = isMobile ? slide.mb_image : slide.image
            const itm_content = isMobile ? slide.mb_image : slide.image

            const finalUrl = buildHeroBannerUrl(
              slide.url,
              index,
              itm_campaign,
              itm_content
            )

            return (
              <CarouselItem
                key={index}
                className='relative h-full w-full p-0'
                role='region'
                aria-label={`Promotional Banner ${index + 1} of ${validSlides.length}`}
              >
                <Link
                  href={finalUrl}
                  target={slide.link_target}
                  className='block h-full w-full'
                  data-tracking-level-1={`banner-${index}`}
                  aria-label={`View promotion ${index + 1}`}
                >
                  {isMobile ? (
                    <Image
                      src={ImageHelper.getImageUrl(
                        slide.mb_image,
                        'm',
                        672,
                        990,
                        80
                      )}
                      alt={`Promotional banner ${index + 1} - mobile view`}
                      width={768}
                      height={393}
                      className='w-full object-cover'
                      priority={index === 0}
                      loading={index === 0 ? 'eager' : 'lazy'}
                      fetchPriority={index === 0 ? 'high' : 'auto'}
                      sizes='(max-width: 768px) 100vw, 768px'
                      quality={80}
                    />
                  ) : (
                    <Image
                      src={ImageHelper.getImageUrl(slide.image)}
                      alt={`Promotional banner ${index + 1} - desktop view`}
                      width={1920}
                      height={787}
                      className='w-full object-cover'
                      priority={index === 0}
                      loading={index === 0 ? 'eager' : 'lazy'}
                      fetchPriority={index === 0 ? 'high' : 'auto'}
                    />
                  )}
                </Link>

                {/* <Link
  href={slide.url}
  target={slide.link_target}
  className='relative block h-full w-full'
/> */}
                {/* <div className='absolute w-full px-6 max-lg:bottom-16 lg:left-15 lg:top-1/2 lg:px-0'>
  <h1 className='text-heading-1 text-light'>Scheme</h1>
  <p className='mt-2 font-sans text-lg text-light lg:mt-3'>
    Text voucher/ưu đãi/khuyến mại
  </p>
  <Button
    variant='white'
    className='mt-6 max-lg:w-full lg:mt-9'
    size={'large'}
    asChild
  >
    <Link href={'/'}>Mua ngay</Link>
  </Button>
</div> */}
              </CarouselItem>
            )
          })}
        </CarouselContent>
        <CarouselNavigation />
      </Carousel>
    </section>
  )
}

function CarouselNavigation() {
  return (
    <>
      <CarouselPrevious
        className='absolute -left-4 top-1/2 h-10 w-10 -translate-y-1/2 border-none invert max-lg:hidden lg:left-1'
        variant='outline-neutral'
        hideIfStart
      />
      <CarouselNext
        className='absolute -right-4 top-1/2 h-10 w-10 -translate-y-1/2 border-none invert max-lg:hidden lg:right-1'
        variant='outline-neutral'
        hideIfEnd
      />
    </>
  )
}
